import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase/config';
import { useLanguage } from '../hooks/useLanguage';
import { useAuth } from '../hooks/useAuth';
import { useParty } from '../hooks/useParty';
import { toast } from 'react-hot-toast';
import MapEditor from '../components/MapEditor';

// MUI components
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  Event as EventIcon,
  LocationOn as LocationOnIcon,
  Description as DescriptionIcon,
  Title as TitleIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Search as SearchIcon
} from '@mui/icons-material';

// Fix Leaflet icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const CreateParty = ({ isEditMode = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { createParty, updateParty } = useParty();
  const { t } = useLanguage();
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [address, setAddress] = useState('');
  const [position, setPosition] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingParty, setFetchingParty] = useState(isEditMode);
  const [geocoding, setGeocoding] = useState(false);
  const [mapCenter, setMapCenter] = useState([51.505, -0.09]);
  const geocodeTimeoutRef = useRef(null);

  // Fetch party data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchParty = async () => {
        try {
          setFetchingParty(true);
          const { data, error } = await supabase
            .from('parties')
            .select('*')
            .eq('id', id)
            .single();
          
          if (error) throw error;
          
          if (data) {
            // Check if user is the host
            if (data.host_id !== user.id && data.host !== user.id) {
              setError('You can only edit parties that you host');
              navigate(`/party/${id}`);
              return;
            }
            
            // Set form data
            setTitle(data.title || '');
            setDescription(data.description || '');
            
            // Parse date and time
            if (data.date) {
              const dateObj = new Date(data.date);
              setDate(dateObj.toISOString().split('T')[0]);
              setTime(dateObj.toTimeString().slice(0, 5));
            }
            
            // Set location
            if (data.location) {
              if (typeof data.location === 'object') {
                setAddress(data.location.address || '');
                if (data.location.coordinates) {
                  const newPosition = {
                    lat: data.location.coordinates.lat,
                    lng: data.location.coordinates.lng
                  };
                  setPosition(newPosition);
                  setMapCenter([newPosition.lat, newPosition.lng]);
                }
              } else {
                setAddress(data.location);
              }
            }
          }
          
          setFetchingParty(false);
        } catch (err) {
          console.error('Error fetching party:', err);
          setError('Failed to load party details');
          setFetchingParty(false);
        }
      };
      
      fetchParty();
    }
  }, [isEditMode, id, user.id, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title || !date || !time || !address || !position) {
      setError(t.fillAllFields);
      return;
    }

    if (!user || !user.id) {
      setError(t.mustBeLoggedIn);
      console.error('User or user.id is missing:', user);
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Combine date and time
      const dateTime = new Date(`${date}T${time}`);
      
      const partyData = {
        title,
        description,
        date: dateTime.toISOString(),
        location: {
          address,
          coordinates: {
            lat: position.lat,
            lng: position.lng
          }
        }
      };
      
      console.log('Party data to be sent:', partyData);
      
      let result;
      
      if (isEditMode) {
        console.log('Updating party with ID:', id);
        result = await updateParty(id, partyData);
      } else {
        result = await createParty(partyData);
      }
      
      console.log('Result from server:', result);
      
      setLoading(false);
      
      if (result && result.id) {
        navigate(`/party/${result.id}`);
      } else {
        setError(t.errorCreatingParty);
      }
    } catch (err) {
      console.error('Error creating/updating party:', err);
      setError(`${t.errorCreatingParty}: ${err.message}`);
      setLoading(false);
    }
  };

  // Geocode the address
  const handleGeocodeAddress = async () => {
    if (!address.trim()) {
      return;
    }
    
    try {
      setGeocoding(true);
      setError('');
      
      // Use Nominatim for geocoding (OpenStreetMap's geocoding service)
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`);
      const data = await response.json();
      
      if (data && data.length > 0) {
        const location = data[0];
        const newPosition = {
          lat: parseFloat(location.lat),
          lng: parseFloat(location.lon)
        };
        
        setPosition(newPosition);
        setMapCenter([newPosition.lat, newPosition.lng]);
      } else {
        // Only show error if the user explicitly clicked the search button
        if (address.trim().length > 0) {
          setError(t.addressNotFound);
        }
      }
      
      setGeocoding(false);
    } catch (err) {
      console.error('Geocoding error:', err);
      setError(`${t.errorGeocodingAddress}: ${err.message}`);
      setGeocoding(false);
    }
  };

  // Handle address change with debounced geocoding
  const handleAddressChange = (e) => {
    const newAddress = e.target.value;
    setAddress(newAddress);
    
    // Clear any existing timeout
    if (geocodeTimeoutRef.current) {
      clearTimeout(geocodeTimeoutRef.current);
    }
    
    // Only set a timeout if the address is long enough and not currently geocoding
    if (newAddress.trim().length > 5 && !geocoding) {
      geocodeTimeoutRef.current = setTimeout(() => {
        handleGeocodeAddress();
      }, 1000);
    }
  };

  // Clean up the timeout on unmount
  useEffect(() => {
    return () => {
      if (geocodeTimeoutRef.current) {
        clearTimeout(geocodeTimeoutRef.current);
      }
    };
  }, []);

  if (!user) {
    return (
      <Container>
        <Box my={4} textAlign="center">
          <Typography variant="h4" component="h1" gutterBottom>
            {t.loginRequired}
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => navigate('/login')}
          >
            {t.goToLogin}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          {isEditMode ? <EditIcon sx={{ mr: 1 }} /> : <EventIcon sx={{ mr: 1 }} />}
          {isEditMode ? t.editParty : t.createNewParty}
        </Typography>
        
        {fetchingParty ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            
            <Grid container spacing={3}>
              {/* Basic Info */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  {t.partyDetails}
                </Typography>
                
                <FormControl fullWidth margin="normal" variant="outlined">
                  <InputLabel htmlFor="title">{t.partyName}</InputLabel>
                  <OutlinedInput
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    startAdornment={
                      <InputAdornment position="start">
                        <TitleIcon />
                      </InputAdornment>
                    }
                    label={t.partyName}
                    required
                  />
                </FormControl>
                
                <FormControl fullWidth margin="normal" variant="outlined">
                  <InputLabel htmlFor="description">{t.partyDescription}</InputLabel>
                  <OutlinedInput
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    startAdornment={
                      <InputAdornment position="start">
                        <DescriptionIcon />
                      </InputAdornment>
                    }
                    label={t.partyDescription}
                    multiline
                    rows={4}
                  />
                </FormControl>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="normal" variant="outlined">
                      <InputLabel htmlFor="date">{t.partyDate}</InputLabel>
                      <OutlinedInput
                        id="date"
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        startAdornment={
                          <InputAdornment position="start">
                            <EventIcon />
                          </InputAdornment>
                        }
                        label={t.partyDate}
                        required
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="normal" variant="outlined">
                      <InputLabel htmlFor="time">{t.partyTime}</InputLabel>
                      <OutlinedInput
                        id="time"
                        type="time"
                        value={time}
                        onChange={(e) => setTime(e.target.value)}
                        label={t.partyTime}
                        required
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              
              {/* Location */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  {t.partyLocation}
                </Typography>
                
                <FormControl fullWidth margin="normal" variant="outlined">
                  <InputLabel htmlFor="address">{t.address}</InputLabel>
                  <OutlinedInput
                    id="address"
                    value={address}
                    onChange={handleAddressChange}
                    startAdornment={
                      <InputAdornment position="start">
                        <LocationOnIcon />
                      </InputAdornment>
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleGeocodeAddress}
                          disabled={geocoding || address.trim().length < 5}
                        >
                          {geocoding ? <CircularProgress size={20} /> : <SearchIcon />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label={t.address}
                    required
                  />
                </FormControl>
                
                <Box sx={{ mt: 2, height: 300, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                  <MapEditor position={position} setPosition={setPosition} />
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {t.clickMapToSetLocation}
                </Typography>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="outlined" 
                color="inherit" 
                onClick={() => navigate(-1)}
                sx={{ mr: 2 }}
              >
                {t.cancel}
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
              >
                {loading ? t.saving : (isEditMode ? t.saveChanges : t.createParty)}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default CreateParty; 